import React, {useState, useEffect} from 'react';
import { UploadIcon,ArrowLeft,LoadingIcon } from "../../../assets/icons/Icons";
import ProgressBar from "../../ProgressBar";
import "../../../assets/scss/components/multi-step-form.scss"
import "../../../assets/scss/components/cardholder/bulk-create-cards.scss"
import {
    CreateCards,
    GetPrograms,
    ValidateBulkCreateCardholderTemplate
} from "../../../api";
import { parseCsv } from "../../../utils/csvParseUtil";
import SelectProgram from "./SelectProgram";
import UploadCsv from "./UploadCsv";
import ValidateCsv from "./ValidateCsv";
import ValidationReport from "./ValidationReport";
import DataOverview from "./DataOverview";
import CardHolderCreationMessage from "./CardHolderCreationMessage";
import {useTranslation} from "react-i18next";


const BulkCreateCards = ({setNewCardHoldersUploaded, setShowBulkCreateCards}) => {

    const { t } = useTranslation();

     const Steps = Object.freeze({
        SELECT_PROGRAM: 1,
        SELECT_FILE: 2,
        VALIDATE_FILE: 3,
        PREVIEW_DATA: 4,
        SUBMIT: 5,
        CONFIRMATION: 6
    });

    const [sendingData, setSendingData] = useState(false);
    const [activeStep, setActiveStep] = useState(Steps.SELECT_PROGRAM);
    const [progress, setProgress] = useState(10);
    const [disabledContinueButton, setDisabledContinueButton] = useState(true);
    const [visibleContinueButton, setVisibleContinueButton] = useState(true);
    const [visibleCreateCardsButton, setVisibleCreateCardsButton] = useState(false);
    const [visibleDismissButton, setVisibleDismissButton] = useState(false);
    const [disabledBackButton, setDisabledBackButton] = useState(true);
    const [visibleBackButton, setVisibleBackButton] = useState(true);
    const [availablePrograms, setAvailablePrograms] = useState([]);
    const [selectedProgram, setSelectedProgram] = useState('');
    const [uploadedTemplate, setUploadedTemplate] = useState(null);
    const [uploadedData, setUploadedData] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [dataValid, setDataValid] = useState(false);
    const [totalCardholders, setTotalCardholders] = useState(0);
    const [totalAmountLoaded, setTotalAmountLoaded] = useState(0);

    const handleBackButtonClick = () => {
        if(activeStep === Steps.PREVIEW_DATA){
            setActiveStep(Steps.SELECT_FILE);
        } else {
            setActiveStep(activeStep - 1);
        }
    }

    const handleCreateCardButtonClick = (e) => {
        e.preventDefault();
        const RequestCreateCards = async () => {
            let file = uploadedTemplate.target.files[0];
            setSendingData(true);
            setNewCardHoldersUploaded(false);
            try {
                await CreateCards(file, selectedProgram);
                setSendingData(false);
                setActiveStep(Steps.CONFIRMATION);
                setUploadedTemplate(null);
                setUploadedData(null);
                setNewCardHoldersUploaded(true);
            } catch (err) {
                console.log(err);
            }
        };
        RequestCreateCards();
    }

    // Default actions on step change
    useEffect(() => {

        // Back button enabled by default on activeStep larger than 1
        if (activeStep > 1) {
            setDisabledBackButton(false);
        } else {
            setDisabledBackButton(true);
        }

        // Select program
        if(activeStep === Steps.SELECT_PROGRAM){
            setProgress(10);
            const fetchPrograms = async () => {
                try {
                    const { data } = await GetPrograms();
                    setAvailablePrograms(data);
                } catch (err) {
                    console.log('Fetching programs error.')
                }
            };
            fetchPrograms();
        }

        // Select file
        if(activeStep === Steps.SELECT_FILE){
            setProgress(36);
            setDisabledContinueButton(true);
        }

        // Validate File
        if(activeStep === Steps.VALIDATE_FILE){
            setDataValid(false);
            if(uploadedTemplate){
                setProgress(50);
                setDisabledContinueButton(true);
                setDisabledBackButton(true);
                const requestValidateTemplate = async () => {
                    try {
                        let file = uploadedTemplate.target.files[0];
                        const response = await ValidateBulkCreateCardholderTemplate(file, selectedProgram);
                        if(response.message === 'Bulk Cardholder CSV validated successfully.'){
                            setDataValid(true);
                        } else if(response.statusCode === 400) {

                            setDataValid(false);

                            const errors = {
                                headers: [],
                                data: []
                            }

                            response.data.forEach(({ cardholder, error }) => {
                                errors.headers = Object.keys(cardholder);
                                errors.headers.push('error_message');
                                let errorMessages = '';
                                error.field_errors.forEach(({ message }, index) => {
                                    if(index > 0){
                                        errorMessages += '<br>';
                                    }
                                    errorMessages += message;

                                })
                                cardholder.error_message = errorMessages;
                                errors.data.push(cardholder);
                            });

                            setValidationErrors(errors);
                        }
                        setActiveStep(Steps.PREVIEW_DATA);

                    } catch (err) {
                        console.log(err);
                    }

                }
                requestValidateTemplate();


            } else  {
                setActiveStep(Steps.SELECT_FILE);
            }
        }

        //Preview uploaded data
        if(activeStep === Steps.PREVIEW_DATA){
            setProgress(65);
        }

        //Submit
        if (activeStep === Steps.SUBMIT){
            setProgress(95);
            setVisibleContinueButton(false);
            setVisibleCreateCardsButton(true);
        } else {
            setVisibleContinueButton(true);
            setVisibleCreateCardsButton(false);
        }

        // Confirmation Message
        if (activeStep === Steps.CONFIRMATION){
            setProgress(100);
            setVisibleContinueButton(false);
            setVisibleBackButton(false);
            setVisibleDismissButton(true);
        }

    }, [activeStep]);

    useEffect(() => {
        if(selectedProgram){
            setDisabledContinueButton(false);
        } else {
            setDisabledContinueButton(true);
        }
    }, [selectedProgram]);

    useEffect(() => {
        if(uploadedTemplate){

            if(uploadedTemplate){
                setDisabledContinueButton(false);
            }else {
                setDisabledContinueButton(true);
            }
            const parseCsvData = async () => {
                const file = uploadedTemplate.target.files[0];
                try {
                    const parsedData = await parseCsv(file);
                    setUploadedData(parsedData);
                    setActiveStep(Steps.VALIDATE_FILE);
                } catch (error) {
                    console.error("Error parsing CSV file:", error);
                }
            }
            parseCsvData();
        }
    }, [uploadedTemplate]);

    useEffect(() => {
        if(dataValid){
            setDisabledContinueButton(false);
        } else {
            setDisabledContinueButton(true);
        }
    }, [dataValid]);

    useEffect(() => {
        if(uploadedData){
            setTotalCardholders(uploadedData?.data?.length);
            let loadedAmount = 0;
            uploadedData?.data?.map((item)=> {
                if(item.load_amount){
                    loadedAmount += Number(item.load_amount);
                }
            });
            setTotalAmountLoaded(loadedAmount);
        }
    }, [uploadedData]);

    return (
        <div className="bulk-create-cards">
            <div className="multistep-form">
                <div className="multistep-form__progress-bar">
                    <ProgressBar
                        title={t("Bulk Create Cards")}
                        steps={[
                            {
                                active: (activeStep >= Steps.SELECT_PROGRAM),
                                name: t("Select a program")
                            },
                            {
                                active: (activeStep >= Steps.SELECT_FILE),
                                name: t("Upload CSV")
                            },
                            {
                                active: (activeStep >= Steps.PREVIEW_DATA),
                                name: t("Validated/Review")
                            },
                            {
                                active: (activeStep >= Steps.CONFIRMATION),
                                name: t("Finalize / Submit")
                            }
                        ]}
                        progress={progress}
                        class="mb-4"
                    />
                </div>
                <div className="multistep-form__content mb-2">
                    {activeStep === Steps.SELECT_PROGRAM && <SelectProgram availablePrograms={availablePrograms} setSelectedProgram={setSelectedProgram} selectedProgram={selectedProgram}/>}
                    {activeStep === Steps.SELECT_FILE && <UploadCsv uploadedTemplate={uploadedTemplate} setUploadedTemplate={setUploadedTemplate} totalCardholders={totalCardholders} setActiveStep={setActiveStep}/>}
                    {activeStep === Steps.VALIDATE_FILE && <ValidateCsv setUploadedTemplate={setUploadedTemplate} setActiveStep={setActiveStep}/>}
                    {activeStep === Steps.PREVIEW_DATA && <ValidationReport dataValid={dataValid} uploadedData={uploadedData} totalAmountLoaded={totalAmountLoaded} validationErrors={validationErrors} totalCardholders={totalCardholders}/>}
                    {activeStep === Steps.SUBMIT && <DataOverview totalCardholders={totalCardholders} totalAmountLoaded={totalAmountLoaded} uploadedData={uploadedData} />}
                    {activeStep === Steps.CONFIRMATION && <CardHolderCreationMessage/>}
                </div>
                <div className="multistep-form__navigation">
                    {visibleBackButton && <button
                        className="btn btn-secondary btn-medium"
                        onClick={handleBackButtonClick}
                        disabled={disabledBackButton}
                    >
                        <ArrowLeft/>{t("Back")}
                    </button>}
                    {visibleContinueButton && <button
                        className="btn btn-primary btn-medium"
                        onClick={e => setActiveStep(activeStep + 1)}
                        disabled={disabledContinueButton}
                    >
                        {t("Continue")}
                    </button>}

                    {visibleCreateCardsButton && <button
                        className={`btn btn-primary btn-medium ${sendingData ? 'btn-loading' : '' }`}
                        onClick={handleCreateCardButtonClick}
                    >
                        {sendingData ? <LoadingIcon/> : <UploadIcon/>}
                        Create Cards
                    </button>}

                    {visibleDismissButton && <div className="text-right" style={{width: '100%'}}>
                        <button
                            className={`btn btn-primary btn-medium`}
                            onClick={e => setShowBulkCreateCards(false)}
                        >{t("Dismiss")}
                        </button>
                    </div>}
                </div>
            </div>

        </div>
    );
};

export default BulkCreateCards;
